/**
 * Swaps the trust strip's badge set when the selected variant changes.
 *
 * Sova, Ultima and Riva each carry a Natural thickness whose claims differ
 * from the rest of the range — Sova reads Pincore Latex and 7/10 there where
 * every other thickness reads Cooler Sleep and 6/10. That is a variant-level
 * difference, but `trust_badges` is a product-level metafield, so the section
 * renders both sets and this decides which one is shown.
 *
 * Both sets are in the DOM from the start. Swapping visibility costs nothing
 * and needs no fetch, and the standard set is the one that stands if this
 * script never runs — the right thing to be wrong about.
 */

(() => {
  if (window.__trustStripReady) return;
  window.__trustStripReady = true;

  // A plain element with a data hook rather than a custom element. The section
  // is a <div> either way, and a Liquid conditional in the *tag name* — which
  // is what a custom element needed here — is both fragile and enough to stop
  // Theme Check parsing the file's snippet references.
  class TrustStrip {
    constructor(root) {
      this.root = root;
      this.match = (root.dataset.naturalMatch || '').trim().toLowerCase();
      this.standard = root.querySelector('[data-trust-set="standard"]');
      this.natural = root.querySelector('[data-trust-set="natural"]');
      if (!this.match || !this.standard || !this.natural) return;

      this.onChange = (event) => this.apply(event.detail && event.detail.variant);
      document.addEventListener('variant:change', this.onChange);

      // The page may already be showing a Natural variant — a shared link
      // carries `?variant=`, and `product-info` does not announce the state it
      // was born in, only changes to it.
      this.apply(this.initialVariant());
    }

    /** The variant the page rendered with, read from product-info's own data. */
    initialVariant() {
      const holder = document.querySelector('[data-variant-data]');
      const id = document.querySelector('[data-variant-id]');
      if (!holder || !id) return null;
      try {
        const variants = JSON.parse(holder.textContent);
        return variants.find((v) => String(v.id) === String(id.value)) || null;
      } catch (error) {
        return null;
      }
    }

    apply(variant) {
      // `options` is the array of selected option values — ["Natural — 6\"",
      // "Queen (78\" x 60\")"]. Matching on any of them rather than on a fixed
      // position means the thickness option can be reordered without this
      // breaking.
      const options = (variant && variant.options) || [];
      const isNatural = options.some(
        (value) => String(value).toLowerCase().includes(this.match)
      );

      this.standard.hidden = isNatural;
      this.natural.hidden = !isNatural;
    }
  }

  const start = () => {
    for (const root of document.querySelectorAll('[data-natural-match]')) {
      new TrustStrip(root);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
